import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, Loading } from "../components/Layout";
import { toast } from "sonner";
import { ShieldCheck, Share2, Users, Plus, Trash2, Save, ChevronRight, CheckSquare } from "lucide-react";

const SCOPE_LABEL = {
  all: "Whole workspace",
  subtree: "Their org unit + everything below it",
  own: "Only their own records",
};

const STEP_HINTS = {
  tree: "Step 1 · Start here. Map your teams, zones or regions. A manager placed on a unit automatically sees everything below it. If you run a single flat team, you can skip straight to step 2.",
  roles: "Step 2 · Define each tier — what data it can see, how senior it is, and which features it can use. Add any custom tiers your organisation needs.",
  people: "Step 3 · Give every person a role and the org unit they belong to.",
  approvals: "Step 4 · Turn on sign-off for sensitive actions, choose who approves, and set escalation timing.",
};

function ScopeBadge({ scope }) {
  const color = scope === "all" ? "#B23A2E" : scope === "subtree" ? "#3E8F99" : "#565E68";
  return (
    <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 border" style={{ color, borderColor: color }}>
      {scope}
    </span>
  );
}

function RoleEditor({ role, catalog, onSaved, onDelete }) {
  const [name, setName] = useState(role.name);
  const [scope, setScope] = useState(role.data_scope);
  const [perms, setPerms] = useState(role.permissions || []);
  const [rank, setRank] = useState(role.rank);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setPerms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const groups = [...new Set(catalog.permissions.map((p) => p.group))];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/roles/${role.key}`, { name, data_scope: scope, permissions: perms, rank });
      toast.success("Role saved");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#DFDAD0] bg-white p-4" data-testid={`role-card-${role.key}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1">
          <input value={name} disabled={role.is_system} onChange={(e) => setName(e.target.value)} data-testid={`role-name-${role.key}`}
            className="text-sm font-medium text-[#1C2530] border border-transparent hover:border-[#DFDAD0] focus:border-[#1C2530] px-1 py-0.5 outline-none disabled:bg-transparent disabled:border-transparent" />
          {role.is_system && <span className="font-mono text-[9px] uppercase text-[#1C2530]/40 border border-[#DFDAD0] px-1">system</span>}
          <span className="font-mono text-[9px] text-[#1C2530]/40">· {role.user_count} user(s)</span>
        </div>
        {!role.is_system && (
          <button onClick={() => onDelete(role)} data-testid={`role-del-${role.key}`} className="text-[#B23A2E] hover:bg-[#B23A2E]/10 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
      <div className="mb-4 pb-4 border-b border-[#F0EDE6]">
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/40 mb-1">A · Data visibility</div>
        <div className="text-[10px] text-[#1C2530]/50 mb-2">Which records this tier can see across the workspace.</div>
        <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid={`role-scope-${role.key}`}
          className="w-full md:w-72 border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
          {catalog.scopes.map((s) => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
        </select>
      </div>
      <div className="mb-4 pb-4 border-b border-[#F0EDE6]">
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/40 mb-1">B · Seniority rank</div>
        <div className="text-[10px] text-[#1C2530]/50 mb-2 leading-relaxed">
          Rank sets management authority. A role can only create or edit other roles at or <b>below</b> its own rank — never above it.
          Higher-ranked people sit above lower ranks in the reporting line (e.g. a rank-50 lead can manage rank-10 reps, but a rep can't edit the lead's role).
        </div>
        <input type="number" value={rank} disabled={role.is_system} onChange={(e) => setRank(Number(e.target.value))} data-testid={`role-rank-${role.key}`}
          className="w-full md:w-40 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530] disabled:bg-[#F5F3EE]" />
      </div>
      <div className="mb-3">
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/40 mb-1">C · Feature permissions</div>
        <div className="text-[10px] text-[#1C2530]/50 mb-2">Which parts of the product this tier can use.</div>
        <div className="space-y-2">
          {groups.map((g) => (
            <div key={g}>
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/40 mb-1">{g}</div>
              <div className="flex flex-wrap gap-1.5">
                {catalog.permissions.filter((p) => p.group === g).map((p) => (
                  <button key={p.key} onClick={() => toggle(p.key)} data-testid={`role-perm-${role.key}-${p.key}`}
                    className={`text-xs px-2 py-1 border transition-colors ${perms.includes(p.key) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "bg-white text-[#1C2530]/60 border-[#DFDAD0] hover:border-[#1C2530]"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[10px] text-[#1C2530]/50 mb-3" data-testid={`role-preview-${role.key}`}>
        This tier sees <b>{SCOPE_LABEL[scope]}</b> · {perms.length} of {catalog.permissions.length} permissions enabled.
      </div>
      <button onClick={save} disabled={busy} data-testid={`role-save-${role.key}`}
        className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-50">
        <Save className="w-3.5 h-3.5" /> Save role
      </button>
    </div>
  );
}

function RoleMatrix({ roles, catalog, onSaved }) {
  const [grid, setGrid] = useState(() => Object.fromEntries(roles.map((r) => [r.key, new Set(r.permissions || [])])));
  const [busy, setBusy] = useState(false);
  const toggle = (rk, pk) => setGrid((g) => { const s = new Set(g[rk]); s.has(pk) ? s.delete(pk) : s.add(pk); return { ...g, [rk]: s }; });
  const save = async () => {
    setBusy(true);
    try {
      for (const r of roles) {
        await api.patch(`/roles/${r.key}`, { name: r.name, data_scope: r.data_scope, rank: r.rank, permissions: [...grid[r.key]] });
      }
      toast.success("Permission matrix saved");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#DFDAD0] bg-white" data-testid="role-matrix">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-[#DFDAD0]">
              <th className="text-left p-2 sticky left-0 bg-white font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Permission</th>
              {roles.map((r) => (
                <th key={r.key} className="p-2 text-center whitespace-nowrap">
                  <div className="font-medium text-[#1C2530]">{r.name}</div>
                  <div className="font-mono text-[8px] uppercase text-[#1C2530]/40">sees {r.data_scope}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.permissions.map((p) => (
              <tr key={p.key} className="border-b border-[#F0EDE6]">
                <td className="p-2 sticky left-0 bg-white">
                  <span className="text-[#1C2530]">{p.label}</span>
                  <span className="ml-1 font-mono text-[8px] uppercase text-[#1C2530]/30">{p.group}</span>
                </td>
                {roles.map((r) => (
                  <td key={r.key} className="p-2 text-center">
                    <input type="checkbox" checked={grid[r.key]?.has(p.key) || false} disabled={r.key === "admin"}
                      onChange={() => toggle(r.key, p.key)} data-testid={`matrix-${r.key}-${p.key}`}
                      className="accent-[#1C2530] disabled:opacity-40" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-[#DFDAD0]">
        <button onClick={save} disabled={busy} data-testid="matrix-save-btn"
          className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> Save matrix
        </button>
        <span className="ml-3 font-mono text-[9px] uppercase text-[#1C2530]/40">Admin tier is locked to full access</span>
      </div>
    </div>
  );
}

export default function Organization() {
  const [tab, setTab] = useState("tree");
  const [roleView, setRoleView] = useState("cards");
  const [catalog, setCatalog] = useState(null);
  const [roles, setRoles] = useState(null);
  const [nodes, setNodes] = useState(null);
  const [users, setUsers] = useState(null);
  const [newRole, setNewRole] = useState({ name: "", data_scope: "subtree", rank: 30 });
  const [newNode, setNewNode] = useState({ name: "", parent_id: "", level_label: "" });
  const [rules, setRules] = useState(null);
  const [ruleActions, setRuleActions] = useState({});
  const [sla, setSla] = useState({ sla_hours: 24, escalate_hours: 0 });

  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data));
  const loadNodes = () => api.get("/org/nodes").then((r) => setNodes(r.data));
  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  const loadRules = () => api.get("/approvals/rules").then((r) => { setRules(r.data.rules); setRuleActions(r.data.actions); setSla({ sla_hours: r.data.sla_hours, escalate_hours: r.data.escalate_hours }); });
  useEffect(() => {
    api.get("/rbac/catalog").then((r) => setCatalog(r.data));
    loadRoles(); loadNodes(); loadUsers(); loadRules();
  }, []);

  const setRule = (a, patch) => setRules((rs) => ({ ...rs, [a]: { ...rs[a], ...patch } }));
  const saveRules = async () => {
    try { await api.put("/approvals/rules", { rules, sla_hours: Number(sla.sla_hours) || 0, escalate_hours: Number(sla.escalate_hours) || 0 }); toast.success("Approval rules saved"); loadRules(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const TEMPLATES = [
    { label: "Regional Manager", name: "Regional Manager", data_scope: "subtree", rank: 40 },
    { label: "Zonal Manager", name: "Zonal Manager", data_scope: "subtree", rank: 60 },
    { label: "Read-only Auditor", name: "Auditor", data_scope: "all", rank: 20 },
  ];

  const createRole = async () => {
    if (!newRole.name.trim()) { toast.error("Name the role"); return; }
    try {
      await api.post("/roles", { ...newRole, permissions: [] });
      toast.success("Role created — set its permissions below");
      setNewRole({ name: "", data_scope: "subtree", rank: 30 });
      loadRoles();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delRole = async (role) => {
    try { await api.delete(`/roles/${role.key}`); toast.success("Role deleted"); loadRoles(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createNode = async () => {
    if (!newNode.name.trim()) { toast.error("Name the unit"); return; }
    try {
      await api.post("/org/nodes", { name: newNode.name, parent_id: newNode.parent_id || null, level_label: newNode.level_label });
      toast.success("Org unit added");
      setNewNode({ name: "", parent_id: "", level_label: "" });
      loadNodes(); loadUsers();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delNode = async (id) => {
    try { await api.delete(`/org/nodes/${id}`); toast.success("Unit removed"); loadNodes(); loadUsers(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const assignUser = async (uid, patch) => {
    try { await api.patch(`/users/${uid}`, patch); toast.success("Updated"); loadUsers(); loadRoles(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // build ordered tree for display
  const tree = [];
  if (nodes) {
    const byParent = {};
    nodes.forEach((n) => { (byParent[n.parent_id || "root"] ||= []).push(n); });
    const walk = (pid, depth) => (byParent[pid] || []).forEach((n) => { tree.push({ ...n, depth }); walk(n.id, depth + 1); });
    walk("root", 0);
  }
  const nodeName = (id) => nodes?.find((n) => n.id === id)?.name || "—";

  if (!catalog || !roles || !nodes || !users) return <Loading />;

  return (
    <>
      <PageHeader eyebrow="ACCESS CONTROL · CUSTOM PER WORKSPACE" title="Organization & Roles" />
      <div className="p-8 space-y-6">
        <div className="flex gap-1 border-b border-[#DFDAD0] overflow-x-auto" data-testid="org-tabs">
          {[["tree", "Org Structure", Share2], ["roles", "Roles & Permissions", ShieldCheck], ["people", "People", Users], ["approvals", "Approval Rules", CheckSquare]].map(([k, label, Icon], i) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`org-tab-${k}`}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === k ? "border-[#B23A2E] text-[#1C2530]" : "border-transparent text-[#1C2530]/50 hover:text-[#1C2530]"}`}>
              <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-semibold ${tab === k ? "bg-[#B23A2E] text-white" : "bg-[#1C2530]/10 text-[#1C2530]"}`}>{i + 1}</span>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <div className="text-xs text-[#1C2530]/60 border-l-2 border-[#B23A2E] pl-3 py-1" data-testid="org-step-hint">
          {STEP_HINTS[tab]}
        </div>

        {tab === "roles" && (
          <div className="space-y-4" data-testid="org-roles">
            <div className="border border-[#DFDAD0] bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">New tier</div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="font-mono text-[9px] uppercase text-[#1C2530]/40 self-center">templates:</span>
                {TEMPLATES.map((t) => (
                  <button key={t.label} onClick={() => setNewRole({ name: t.name, data_scope: t.data_scope, rank: t.rank })} data-testid={`role-template-${t.data_scope}-${t.rank}`}
                    className="text-xs px-2 py-1 border border-[#DFDAD0] text-[#1C2530]/60 hover:border-[#1C2530] hover:text-[#1C2530]">{t.label}</button>
                ))}
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} data-testid="new-role-name"
                  placeholder="e.g. Zonal Manager" className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
                <select value={newRole.data_scope} onChange={(e) => setNewRole({ ...newRole, data_scope: e.target.value })} data-testid="new-role-scope"
                  className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                  {catalog.scopes.map((s) => <option key={s} value={s}>{SCOPE_LABEL[s]}</option>)}
                </select>
                <input type="number" value={newRole.rank} onChange={(e) => setNewRole({ ...newRole, rank: Number(e.target.value) })} data-testid="new-role-rank"
                  placeholder="rank" className="w-24 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
                <button onClick={createRole} data-testid="new-role-btn"
                  className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]"><Plus className="w-3.5 h-3.5" /> Add tier</button>
              </div>
            </div>
            <div className="flex items-center gap-1 border border-[#DFDAD0] w-fit" data-testid="role-view-toggle">
              {["cards", "matrix"].map((v) => (
                <button key={v} onClick={() => setRoleView(v)} data-testid={`role-view-${v}`}
                  className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${roleView === v ? "bg-[#1C2530] text-[#F5F3EE]" : "text-[#1C2530]/60 hover:text-[#1C2530]"}`}>{v}</button>
              ))}
            </div>
            {roleView === "cards" ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {roles.map((r) => <RoleEditor key={r.key} role={r} catalog={catalog} onSaved={loadRoles} onDelete={delRole} />)}
              </div>
            ) : (
              <RoleMatrix key={roles.map((r) => r.key).join(",")} roles={roles} catalog={catalog} onSaved={loadRoles} />
            )}
          </div>
        )}

        {tab === "tree" && (
          <div className="space-y-4" data-testid="org-tree">
            <div className="border border-[#DFDAD0] bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-1">New org unit</div>
              <div className="text-xs text-[#1C2530]/50 mb-3">Build any depth — e.g. Zone → Region → Team. Managers on a unit see everything below it.</div>
              <div className="flex flex-col md:flex-row gap-2">
                <input value={newNode.name} onChange={(e) => setNewNode({ ...newNode, name: e.target.value })} data-testid="new-node-name"
                  placeholder="Unit name (e.g. West Zone)" className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
                <input value={newNode.level_label} onChange={(e) => setNewNode({ ...newNode, level_label: e.target.value })} data-testid="new-node-level"
                  placeholder="Level label (e.g. Zone)" className="w-40 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
                <select value={newNode.parent_id} onChange={(e) => setNewNode({ ...newNode, parent_id: e.target.value })} data-testid="new-node-parent"
                  className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                  <option value="">Top level (no parent)</option>
                  {nodes.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
                <button onClick={createNode} data-testid="new-node-btn"
                  className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]"><Plus className="w-3.5 h-3.5" /> Add</button>
              </div>
            </div>
            <div className="border border-[#DFDAD0] bg-white p-4" data-testid="org-tree-list">
              {tree.length === 0 ? <div className="font-mono text-xs text-[#1C2530]/40">NO_UNITS_YET</div> : tree.map((n) => (
                <div key={n.id} className="flex items-center justify-between py-1.5 border-b border-[#F0EDE6] last:border-0" style={{ paddingLeft: n.depth * 20 }} data-testid={`node-row-${n.id}`}>
                  <div className="flex items-center gap-2">
                    {n.depth > 0 && <ChevronRight className="w-3 h-3 text-[#1C2530]/30" />}
                    <span className="text-sm text-[#1C2530]">{n.name}</span>
                    {n.level_label && <span className="font-mono text-[9px] uppercase text-[#1C2530]/40 border border-[#DFDAD0] px-1">{n.level_label}</span>}
                    <span className="font-mono text-[9px] text-[#1C2530]/40">{n.user_count} user(s)</span>
                  </div>
                  <button onClick={() => delNode(n.id)} data-testid={`node-del-${n.id}`} className="text-[#B23A2E] hover:bg-[#B23A2E]/10 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "people" && (
          <div className="border border-[#DFDAD0] bg-white p-4" data-testid="org-people">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Assign each person a role & org unit</div>
            <div className="space-y-2">
              {users.filter((u) => u.role !== "super_admin").map((u) => (
                <div key={u.id} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center py-2 border-b border-[#F0EDE6] last:border-0" data-testid={`person-row-${u.id}`}>
                  <div>
                    <div className="text-sm text-[#1C2530]">{u.name}</div>
                    <div className="font-mono text-[10px] text-[#1C2530]/40">{u.email}</div>
                  </div>
                  <select value={u.role} onChange={(e) => assignUser(u.id, { role: e.target.value })} data-testid={`person-role-${u.id}`}
                    className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                    {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                  </select>
                  <select value={u.org_node_id || ""} onChange={(e) => assignUser(u.id, { org_node_id: e.target.value })} data-testid={`person-node-${u.id}`}
                    className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                    <option value="">No org unit</option>
                    {nodes.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {nodes.length === 0 && <div className="mt-3 text-xs text-[#1C2530]/50">Tip: build your org structure first so territory-scoped roles know what to show.</div>}
          </div>
        )}

        {tab === "approvals" && rules && (
          <div className="space-y-3" data-testid="org-approvals">
            <div className="text-xs text-[#1C2530]/50">Turn on sign-off for sensitive actions. <b>Admins are exempt.</b> If the chosen approver would be the requester, it auto-escalates to an Admin.</div>
            <div className="border border-[#DFDAD0] bg-white p-4" data-testid="sla-config">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">SLA & escalation</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs text-[#1C2530]/60">
                  Re-notify approver if pending longer than (hours)
                  <input type="number" min="0" value={sla.sla_hours} onChange={(e) => setSla({ ...sla, sla_hours: e.target.value })} data-testid="sla-hours"
                    className="w-full mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
                </label>
                <label className="text-xs text-[#1C2530]/60">
                  Escalate to an Admin after (hours) — 0 = off
                  <input type="number" min="0" value={sla.escalate_hours} onChange={(e) => setSla({ ...sla, escalate_hours: e.target.value })} data-testid="escalate-hours"
                    className="w-full mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
                </label>
              </div>
            </div>
            {Object.keys(ruleActions).map((a) => (
              <div key={a} className="border border-[#DFDAD0] bg-white p-4" data-testid={`rule-${a}`}>
                <label className="flex items-center gap-2 text-sm font-medium text-[#1C2530] cursor-pointer">
                  <input type="checkbox" checked={!!rules[a]?.enabled} onChange={(e) => setRule(a, { enabled: e.target.checked })} data-testid={`rule-enabled-${a}`} />
                  {ruleActions[a]}
                </label>
                {rules[a]?.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-6 mt-2">
                    <select value={rules[a]?.approver_type || "role"} onChange={(e) => setRule(a, { approver_type: e.target.value })} data-testid={`rule-type-${a}`}
                      className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                      <option value="role">Approver is a role / tier</option>
                      <option value="user">Approver is a specific person</option>
                    </select>
                    {rules[a]?.approver_type === "user" ? (
                      <select value={rules[a]?.approver_user || ""} onChange={(e) => setRule(a, { approver_user: e.target.value })} data-testid={`rule-user-${a}`}
                        className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                        <option value="">Select person…</option>
                        {users.filter((u) => u.role !== "super_admin").map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                      </select>
                    ) : (
                      <select value={rules[a]?.approver_role || "admin"} onChange={(e) => setRule(a, { approver_role: e.target.value })} data-testid={`rule-role-${a}`}
                        className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
                        {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>
            ))}
            <button onClick={saveRules} data-testid="save-rules-btn"
              className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]">
              <Save className="w-3.5 h-3.5" /> Save approval rules
            </button>
          </div>
        )}
      </div>
    </>
  );
}
